import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../axios';
import {
  PubMedArticle,
  PubMedArticleTranslation,
  AustrianPubMedArticle,
  AustrianPubMedSearchResponse,
} from '../types';
import { getRandomTopicImage } from '../utils/randomTopicImage';
import { RootState, useAppSelector } from '../store';
import PaginationControls from './PaginationControls';
import { ArticleGrid } from './ArticleGrid';
import { FeaturedLayout } from './FeaturedLayout';
import AwayClickablePaywallModal from './AwayClickablePaywallModal';
import { AustrianArticleCard } from './AustrianArticleCard';
import { PubMedCard } from './PubMedCard';
import { useAuthInitialization } from '../hooks/useAuthInitialization';

interface RubricArticles {
  rubricId: number;
  rubricName: string;
  articles: PubMedArticle[];
  currentPage: number;
  totalPages: number;
}

interface AustrianRubricArticles {
  rubricId: number;
  rubricName: string;
  articles: AustrianPubMedArticle[];
  currentPage: number;
  totalPages: number;
}

const useResponsivePageSize = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate page size based on screen width
  if (windowWidth >= 1536) {
    return 8; // 2xl: 4 columns, 2 rows = 8 articles
  } else if (windowWidth >= 1024) {
    return 6; // lg: 3 columns, 2 rows = 6 articles
  } else if (windowWidth >= 640) {
    return 4; // sm: 2 columns, 2 rows = 4 articles
  } else {
    return 3; // mobile: 1 column, 3 rows = 3 articles
  }
};

const PubMedDisplay = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { rubricId: rubricIdParam } = useParams();
  const { t } = useTranslation();

  // Redux state
  const currentLanguage = useSelector((state: RootState) => state.language.currentLanguage);
  
  const { user, isAuthenticated, loading: authLoading } = useAppSelector((state) => state.auth)

  // Access control - use useMemo to ensure it recalculates when user changes
  const hasFullAccess = useMemo(() => {
    return user?.roles?.includes('PERMISSION_ADMIN') || 
           user?.roles?.includes('PERMISSION_PAID_ACCESS');
  }, [user?.roles]);


  // Featured layout state (fallback if no rubrics)
  const [featuredArticle, setFeaturedArticle] = useState<PubMedArticle | null>(null);
  const [sideArticles, setSideArticles] = useState<PubMedArticle[]>([]);
  const [gridArticles, setGridArticles] = useState<PubMedArticle[]>([]);
  const { isFullyReady, userRubrics } = useAuthInitialization();

  // Removed unused initialLoadComplete state

  
  // Main content state with pagination
  const [latestRubricsData, setLatestRubricsData] = useState<RubricArticles[]>([]);
  const [topCitedRubricsData, setTopCitedRubricsData] = useState<RubricArticles[]>([]);
  const [showPaywall, setShowPaywall] = useState(false);

  // Austrian articles state
  const [austrianLatestRubricsData, setAustrianLatestRubricsData] = useState<AustrianRubricArticles[]>([]);
  const [austrianArticles, setAustrianArticles] = useState<AustrianPubMedArticle[]>([]);
  // Removed unused austrianLoading state
  const [austrianPage, setAustrianPage] = useState(0);
  const [austrianTotalPages, setAustrianTotalPages] = useState(0);
  
  // Different page sizes for paying vs non-paying users
  const responsivePageSize = useResponsivePageSize();
  // For paying users, show 4 items on 2xl screens (grid-cols-4), otherwise keep 3
  const is2xlScreen = responsivePageSize === 8; // useResponsivePageSize returns 8 on 2xl
  const SERVER_SIZE_PER_RUBRIC = hasFullAccess ? (is2xlScreen ? 4 : 3) : responsivePageSize;
  const AUST_PAGE_SIZE = hasFullAccess ? (is2xlScreen ? 4 : 3) : responsivePageSize;

  // UI state
  const [loading, setLoading] = useState(true);
  const [isShowingRandomRubric, setIsShowingRandomRubric] = useState(false);
  const [randomRubricName, setRandomRubricName] = useState<string>('');
  // Removed unused isPaywallModalOpen state

  // URL params
  const selectedRubricId = useMemo(() => {
    // Prefer route param if present, else fall back to query param
    if (rubricIdParam) {
      const num = Number(rubricIdParam);
      return Number.isFinite(num) ? num : null;
    }
    const val = searchParams.get('rubric');
    return val ? Number(val) : null;
  }, [rubricIdParam, searchParams]);

  
  // Helper function to fetch articles for a specific rubric
  const fetchArticlesForRubric = async (
    rubricId: number,
    rubricName: string,
    endpoint: 'latest' | 'top-cited',
    page: number
  ) => {
    const url = `/api/pubmedarticles/rubric/${rubricId}/${endpoint}`;
    const resp = await axios.get(url, {
      params: { page, size: SERVER_SIZE_PER_RUBRIC },
    });

    let articles: PubMedArticle[] = [];
    let totalPages = 1;

    if (resp.data?.content) {
      articles = resp.data.content;
      totalPages = resp.data.totalPages;
    } else if (Array.isArray(resp.data)) {
      articles = resp.data;
      totalPages = Math.ceil(resp.data.length / SERVER_SIZE_PER_RUBRIC);
    }
    console.log("articles: ", articles)


    return { articles, totalPages };
  };

  // Helper function to fetch Austrian articles for a specific rubric
  const fetchAustrianArticlesForRubric = async (
    rubricId: number,
    rubricName: string,
    endpoint: 'latest' | 'top-cited',
    page: number
  ) => {
    const url = `/api/austrian-articles/rubric/${rubricId}/${endpoint}`;
    const resp = await axios.get(url, {
      params: { page, size: SERVER_SIZE_PER_RUBRIC },
    });

    let articles: AustrianPubMedArticle[] = [];
    let totalPages = 1;

    if (resp.data?.content) {
      articles = resp.data.content;
      totalPages = resp.data.totalPages;
    } else if (Array.isArray(resp.data)) {
      articles = resp.data;
      totalPages = Math.ceil(resp.data.length / SERVER_SIZE_PER_RUBRIC);
    }

    return { articles, totalPages };
  };

  // Main fetch function for latest and top-cited articles
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      let rubricIds: number[] = [];
      console.log('rubrics: ', userRubrics)
      if (selectedRubricId) {
        rubricIds = [selectedRubricId];
        setIsShowingRandomRubric(false);
        setRandomRubricName('');
      } else if (!hasFullAccess || (hasFullAccess && userRubrics.length === 0)) {
        // For non-paying users OR paying users with no selected rubrics, get a random rubric
        try {
          const randomRubricResp = await axios.get('/api/rubrics/random');
          if (randomRubricResp.data) {
            rubricIds = [randomRubricResp.data.id];
            setIsShowingRandomRubric(true);
            setRandomRubricName(randomRubricResp.data.name);
            console.log('Using random rubric:', randomRubricResp.data);
          }
        } catch (error) {
          console.warn('Failed to get random rubric:', error);
          // If random rubric fails and user has rubrics, fall back to them
          if (userRubrics.length > 0) {
            rubricIds = userRubrics.map(r => r.id);
            setIsShowingRandomRubric(false);
            setRandomRubricName('');
          }
        }
      } else if (userRubrics.length > 0) {
        // For paying users with selected rubrics, use all their selected rubrics
        rubricIds = userRubrics.map(r => r.id);
        setIsShowingRandomRubric(false);
        setRandomRubricName('');
      }

      // If no rubrics, show featured layout
      if (rubricIds.length === 0) {
        setIsShowingRandomRubric(false);
        setRandomRubricName('');
        const resp = await axios.get<PubMedArticle[]>('/api/pubmedarticles/top-by-rubric/today');
        const articles = resp.data || [];
        if (articles.length > 0) {
          const sorted = [...articles].sort(
            (a, b) => (b.citationCount || 0) - (a.citationCount || 0)
          );
          console.log("articles: ", articles)

          setFeaturedArticle(sorted[0]);
          setSideArticles(sorted.slice(1, 3));
          setGridArticles(sorted.slice(3));
        } else {
          setFeaturedArticle(null);
          setSideArticles([]);
          setGridArticles([]);
        }
        setLatestRubricsData([]);
        setTopCitedRubricsData([]);
        return;
      }

      const newLatestData: RubricArticles[] = [];
      const newTopData: RubricArticles[] = [];
      const newAustrianLatestData: AustrianRubricArticles[] = [];

      // Fetch data for each rubric
      for (const rubId of rubricIds) {
        let foundRubric = userRubrics.find(r => r.id === rubId);
        let name = foundRubric ? foundRubric.name : `Rubric #${rubId}`;
        
        // If rubric not found in user's rubrics (e.g., random rubric), fetch it
        if (!foundRubric) {
          try {
            const rubricResp = await axios.get(`/api/rubrics/${rubId}`);
            if (rubricResp.data) {
              name = rubricResp.data.name;
            }
          } catch (error) {
            console.warn(`Failed to fetch rubric name for ID ${rubId}:`, error);
          }
        }

        // Fetch latest articles
        const { articles: latestArticles, totalPages: latestPages } = 
          await fetchArticlesForRubric(rubId, name, 'latest', 0);
        
        // Fetch top-cited articles
        const { articles: topArticles, totalPages: topPages } = 
          await fetchArticlesForRubric(rubId, name, 'top-cited', 0);

        newLatestData.push({
          rubricId: rubId,
          rubricName: name,
          articles: latestArticles,
          currentPage: 0,
          totalPages: latestPages
        });

        newTopData.push({
          rubricId: rubId,
          rubricName: name,
          articles: topArticles,
          currentPage: 0,
          totalPages: topPages
        });

        // Fetch Austrian articles for paid users
        if (hasFullAccess) {
          try {
            // Fetch Austrian latest articles
            const { articles: austrianLatestArticles, totalPages: austrianLatestPages } = 
              await fetchAustrianArticlesForRubric(rubId, name, 'latest', 0);

            newAustrianLatestData.push({
              rubricId: rubId,
              rubricName: name,
              articles: austrianLatestArticles,
              currentPage: 0,
              totalPages: austrianLatestPages
            });
          } catch (error) {
            console.warn(`No Austrian articles found for rubric ${rubId}:`, error);
            // Add empty data to maintain consistent structure
            newAustrianLatestData.push({
              rubricId: rubId,
              rubricName: name,
              articles: [],
              currentPage: 0,
              totalPages: 0
            });
          }
        }
      }

      // Clear featured layout when showing rubrics
      setFeaturedArticle(null);
      setSideArticles([]);
      setGridArticles([]);

      setLatestRubricsData(newLatestData);
      setTopCitedRubricsData(newTopData);
      setAustrianLatestRubricsData(newAustrianLatestData);
      
      // Clear old Austrian articles when displaying rubric-specific data
      if (hasFullAccess && rubricIds.length > 0) {
        setAustrianArticles([]);
        setAustrianTotalPages(0);
      }

    } catch (err) {
      console.error('Error fetching PubMed articles:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedRubricId, userRubrics]);



  // Handle pagination for both latest and top-cited sections
  const handlePageChange = async (
    rubricId: number,
    newPage: number,
    type: 'latest' | 'top-cited'
  ) => {
    const dataArray = type === 'latest' ? latestRubricsData : topCitedRubricsData;
    const setDataArray = type === 'latest' ? setLatestRubricsData : setTopCitedRubricsData;
    
    const rubricIndex = dataArray.findIndex(r => r.rubricId === rubricId);
    if (rubricIndex === -1) return;

    const rubric = dataArray[rubricIndex];
    const { articles, totalPages } = await fetchArticlesForRubric(
      rubricId,
      rubric.rubricName,
      type,
      newPage
    );

    const newData = [...dataArray];
    newData[rubricIndex] = {
      ...rubric,
      articles,
      currentPage: newPage,
      totalPages
    };

    setDataArray(newData);
  };

  // Handle pagination for Austrian articles by rubric
  const handleAustrianPageChange = async (
    rubricId: number,
    newPage: number
  ) => {
    const dataArray = austrianLatestRubricsData;
    const setDataArray = setAustrianLatestRubricsData;
    
    const rubricIndex = dataArray.findIndex(r => r.rubricId === rubricId);
    if (rubricIndex === -1) return;

    const rubric = dataArray[rubricIndex];
    
    try {
      const { articles, totalPages } = await fetchAustrianArticlesForRubric(
        rubricId,
        rubric.rubricName,
        'latest',
        newPage
      );

      const newData = [...dataArray];
      newData[rubricIndex] = {
        ...rubric,
        articles: articles,
        currentPage: newPage,
        totalPages
      };

      setDataArray(newData);
    } catch (error) {
      console.warn(`Error paginating Austrian articles for rubric ${rubricId}:`, error);
    }
  };

  // Fetch Austrian articles by rubric for paying users or general for non-paying
  const fetchAustrianArticles = useCallback(async () => {
    try {
      let url = '/api/austrian-articles';
      let params: any = {
        page: austrianPage,
        size: AUST_PAGE_SIZE,
      };

      // If user has full access and a rubric is selected, fetch Austrian articles for that rubric
      if (hasFullAccess && selectedRubricId) {
        url = `/api/austrian-articles/rubric/${selectedRubricId}/latest`;
      }

      const resp = await axios.get<AustrianPubMedSearchResponse>(url, { params });
      if (resp.data?.content) {
        setAustrianArticles(resp.data.content);
        console.log("austrian: ", resp.data.content)
        setAustrianTotalPages(resp.data.totalPages);
      }
    } catch (error) {
      console.error('Error fetching Austrian articles:', error);
    }
  }, [austrianPage, hasFullAccess, selectedRubricId, AUST_PAGE_SIZE]);

  // Initial data fetch
  useEffect(() => {
    // For non-authenticated users, start loading immediately
    // For authenticated users, wait for full readiness
    const canStartLoading = !isAuthenticated || isFullyReady;
    
    if (canStartLoading) {
      const loadAllContent = async () => {
        setLoading(true);
        try {
          await Promise.all([
            fetchArticles(),
            fetchAustrianArticles()
          ]);
        } finally {
          setLoading(false);
        }
      };
      loadAllContent();
    }
  }, [isAuthenticated, isFullyReady, userRubrics, fetchArticles, fetchAustrianArticles]);
  
  // Event handlers (removed unused handlePaywallModalClose)

  const handleRubricClick = useCallback(
    (rubricId: number | undefined) => {
      if (!rubricId) return;

      if (hasFullAccess) {
        const newRubricId = rubricId === selectedRubricId ? null : rubricId;
        // Use route-based navigation for consistency with routes
        if (newRubricId) {
          navigate(`/rubric/${newRubricId}`, { replace: true });
        } else {
          navigate(`/`, { replace: true });
        }
      } else {
        setShowPaywall(true);
      }
    },
    [hasFullAccess, selectedRubricId, navigate]
  );

  const handleReadFullStudy = useCallback(
    (pmid: string, isAustrian: boolean = false) => {
      if (isAustrian && !hasFullAccess) {
        setShowPaywall(true);
        return;
      }
      navigate(`/pubmed/${pmid}`);
    },
    [navigate, hasFullAccess]
  );

  const handleGeneralAustrianPageChange = (newPage: number) => setAustrianPage(newPage);

  // Helper functions
  const getTranslation = useCallback(
    (article: PubMedArticle | AustrianPubMedArticle): PubMedArticleTranslation | null => {
      if (!article.translations?.length) return null;
      return article.translations.find(t => t.languageCode === currentLanguage) || null;
    },
    [currentLanguage]
  );

  // @deprecated Backend topic image loading is deprecated, use random topic images instead
  const getImageUrl = (name: string | undefined, size: 'big' | 'small' = 'big') => {
    return getRandomTopicImage();
  };

  const isRecent = (article: PubMedArticle) =>
    new Date(article.publicationDate).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000;

  // Removed unused isRecentAustrian function
  

  // For non-authenticated users, we can start loading immediately
  // For authenticated users, wait for auth to be fully ready
  const shouldShowLoading = loading || (isAuthenticated && (!isFullyReady || authLoading === 'pending'));
  
  if (shouldShowLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        {t('common.loading')}...
      </div>
    );
  }

  return (
    <div>
      {showPaywall && <AwayClickablePaywallModal onClose={() => setShowPaywall(false)} />}

      {/* Access upgrade banner */}
      {(!hasFullAccess || isShowingRandomRubric) && (
        <div className="lg:mb-12 mb-4 rounded-lg lg:p-2">
          <p className="text-[#49ABDB] font-medium mb-2">
            {isShowingRandomRubric ? (
              <>
                {t('pubmed.showingRandomRubric', { rubricName: randomRubricName })} -{' '}
                {isAuthenticated 
                  ? hasFullAccess 
                    ? t('pubmed.randomRubricPayingUser')
                    : t('pubmed.randomRubricNonPayingUser')
                  : t('pubmed.randomRubricNotLoggedIn')
                }
              </>
            ) : (
              t('pubmed.limitedAccess')
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            {!isAuthenticated ? (
              <>
                <a
                  href="/login"
                  className="inline-block bg-[#49ABDB] text-white px-6 py-2 rounded-full font-medium hover:bg-[#3891c0] transition-colors"
                >
                  {t('auth.buttons.signIn')}
                </a>
                <a
                  href="/signup"
                  className="inline-block bg-gradient-to-r from-[#49ABDB] to-[#A0C9DA] text-white px-6 py-2 rounded-full font-medium hover:opacity-90 transition-opacity ml-2"
                >
                  {t('auth.buttons.signUp')}
                </a>
              </>
            ) : !hasFullAccess ? (
              <a
                href="/profile"
                className="inline-block bg-gradient-to-r from-[#49ABDB] to-[#A0C9DA] text-white px-6 py-2 rounded-full font-medium hover:opacity-90 transition-opacity"
              >
                {t('profile.upgradeSubscription')}
              </a>
            ) : (
              <a
                href="/profile"
                className="inline-block bg-[#49ABDB] text-white px-6 py-2 rounded-full font-medium hover:bg-[#3891c0] transition-colors"
              >
                {t('profile.selectRubrics')}
              </a>
            )}
          </div>
        </div>
      )}
  
      {/* Main content */}
      {latestRubricsData.length === 0 && topCitedRubricsData.length === 0 ? (
        // Featured layout (fallback)
        featuredArticle && (
          <FeaturedLayout
            featuredArticle={featuredArticle}
            sideArticles={sideArticles}
            gridArticles={gridArticles}
            onRubricClick={handleRubricClick}
            onReadStudy={handleReadFullStudy}
            getTranslation={getTranslation}
            getImageUrl={getImageUrl}
            isRecent={isRecent}
            t={t}
          />
        )
      ) : (
        <>
          {hasFullAccess ? (
            // Single row layout for paying users
            <>
              {/* Austrian Studies - First section for paying users */}
              {austrianLatestRubricsData.some(r => r.articles.length > 0) && (
                <div className="mb-8">
                  <h2 className="text-2xl xl:text-4xl font-bold text-[#49ABDB] mb-6">
                    {t('pubmed.austrianStudies')}
                  </h2>
                  
                  {/* Austrian Latest Articles */}
                  {austrianLatestRubricsData.filter(r => r.articles.length > 0).map(({ rubricId, rubricName, articles, currentPage, totalPages }) => (
                    <div key={`austrian-latest-${rubricId}`} className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-700 mb-3">
                        {rubricName} - {t('pubmed.latestStudies')}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                        {articles.map((article) => (
                          <AustrianArticleCard
                            key={article.pmid}
                            article={article}
                            onRubricClick={handleRubricClick}
                            onReadStudy={handleReadFullStudy}
                            getTranslation={getTranslation}
                            getImageUrl={getImageUrl}
                            rubricName={rubricName}
                            t={t}
                          />
                        ))}
                      </div>
                      <div className="mt-4">
                        <PaginationControls
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={(newPage) => handleAustrianPageChange(rubricId, newPage)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Top Cited Studies - Second section */}
              {topCitedRubricsData.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl xl:text-4xl font-bold text-[#49ABDB] mb-6">
                    {t('pubmed.topCitedStudies')}
                  </h2>
                  {topCitedRubricsData.map(({ rubricId, articles, currentPage, totalPages }) => (
                    <div key={rubricId} className="mb-8">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                        {articles.map((article) => (
                          <PubMedCard
                            key={article.pmid}
                            article={article}
                            onRubricClick={handleRubricClick}
                            onReadStudy={handleReadFullStudy}
                            getTranslation={getTranslation}
                            getImageUrl={getImageUrl}
                            isRecent={isRecent}
                            t={t}
                          />
                        ))}
                      </div>
                      <div className="mt-4">
                        <PaginationControls
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={(newPage) => handlePageChange(rubricId, newPage, 'top-cited')}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Latest Studies - Third section */}
              {latestRubricsData.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl xl:text-4xl font-bold text-[#49ABDB] mb-6">
                    {t('pubmed.latestStudies')}
                  </h2>
                  {latestRubricsData.map(({ rubricId, articles, currentPage, totalPages }) => (
                    <div key={rubricId} className="mb-8">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                        {articles.map((article) => (
                          <PubMedCard
                            key={article.pmid}
                            article={article}
                            onRubricClick={handleRubricClick}
                            onReadStudy={handleReadFullStudy}
                            getTranslation={getTranslation}
                            getImageUrl={getImageUrl}
                            isRecent={isRecent}
                            t={t}
                          />
                        ))}
                      </div>
                      <div className="mt-4">
                        <PaginationControls
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={(newPage) => handlePageChange(rubricId, newPage, 'latest')}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            // Original multi-row layout for non-paying users
            <>
              {/* Latest Studies */}
              <h2 className="text-2xl xl:text-4xl font-bold text-[#49ABDB] mb-6">
                {t('pubmed.latestStudies')}
              </h2>
              {latestRubricsData.map(({ rubricId, articles, currentPage, totalPages }) => (
                <div key={rubricId} className="mb-8">
                  <ArticleGrid
                    articles={articles}
                    onRubricClick={handleRubricClick}
                    onReadStudy={handleReadFullStudy}
                    getTranslation={getTranslation}
                    getImageUrl={getImageUrl}
                    isRecent={isRecent}
                    t={t}
                  />
                  <div className="mt-4">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(newPage) => handlePageChange(rubricId, newPage, 'latest')}
                    />
                  </div>
                </div>
              ))}
      
              {/* Top Cited Studies */}
              <h2 className="text-2xl xl:text-4xl font-bold text-[#49ABDB] mb-6 mt-8">
                {t('pubmed.topCitedStudies')}
              </h2>
              {topCitedRubricsData.map(({ rubricId, articles, currentPage, totalPages }) => (
                <div key={rubricId} className="mb-8">
                  <ArticleGrid
                    articles={articles}
                    onRubricClick={handleRubricClick}
                    onReadStudy={handleReadFullStudy}
                    getTranslation={getTranslation}
                    getImageUrl={getImageUrl}
                    isRecent={isRecent}
                    t={t}
                  />
                  <div className="mt-4">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(newPage) => handlePageChange(rubricId, newPage, 'top-cited')}
                    />
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
  
      {/* Austrian Articles Section - Only show here for non-paying users */}
      {!hasFullAccess && austrianArticles.length > 0 && (
        <div className="mt-16">
          <h2 className="text-2xl xl:text-4xl font-bold text-[#49ABDB] mb-8">
            {t('pubmed.austrianStudies')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            {austrianArticles.map((article) => (
              <AustrianArticleCard
                key={article.pmid}
                article={article}
                onRubricClick={handleRubricClick}
                onReadStudy={handleReadFullStudy}
                getTranslation={getTranslation}
                getImageUrl={getImageUrl}
                t={t}
              />
            ))}
          </div>
          <div className="mt-4">
            <PaginationControls
              currentPage={austrianPage}
              totalPages={austrianTotalPages}
              onPageChange={handleGeneralAustrianPageChange}
            />
          </div>
        </div>
      )}
  
      {/* Upgrade Modal */}
    </div>
  );

};
export default PubMedDisplay;
